import { getCurrentUser } from 'aws-amplify/auth';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router';

import type * as SentryModule from '@sentry/react';

import './styles/tailwind.css';
import App from './App';

setTimeout(() => { import('./config/i18n'); }, 100);

const nakedDomain = import.meta.env.VITE_NAKED_DOMAIN || 'givelaud.com';
const wwwDomain = `www.${nakedDomain}`;

if (window.location.hostname === nakedDomain) {
    window.location.assign(window.location.href.replace(nakedDomain, wwwDomain));
}

// Lazy-load Sentry so it doesn't block initial render (~900KB)
let sentryModule: typeof SentryModule | null = null;
if (process.env.NODE_ENV !== 'development') {
    import('@sentry/react').then((mod) => {
        sentryModule = mod;
        mod.init({
            dsn: import.meta.env.VITE_SENTRY_DSN || 'https://5db4d5ca80874cb991737432f4898013@o526321.ingest.sentry.io/5641704',
            integrations: [mod.browserTracingIntegration()],
            tracesSampleRate: 1.0,
        });
    });
}

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        if (sentryModule) {
            sentryModule.captureException(error, { extra: { componentStack: info.componentStack } });
        }
    }

    render() {
        if (this.state.hasError) {
            return <p>Sorry, something went wrong. Please try again.</p>;
        }
        return this.props.children;
    }
}

function mountApp() {
    const container = document.getElementById('root')!;
    const root = createRoot(container);
    root.render(
        <React.StrictMode>
            <ErrorBoundary>
                <Router>
                    <App />
                </Router>
            </ErrorBoundary>
        </React.StrictMode>,
    );
}

// SSR pages (e.g. /award/*) are fully rendered server-side.
// Don't mount React or it will replace the SSR content with a broken
// client-side render that requires authentication. In-app links include
// ?ref=app so logged-in users still get the full React experience,
// but only if they're actually authenticated.
const isSSR = document.querySelector('meta[name="generatedOn"][content="server"]');
const isAppNavigation = new URLSearchParams(window.location.search).has('ref', 'app');
if (!isSSR) {
    mountApp();
} else if (isAppNavigation) {
    getCurrentUser().then(() => mountApp()).catch(() => {
        // Not authenticated — keep the SSR content as-is
    });
}
